import { useEffect, useRef, useState } from 'react';
import type { BenchView } from '@gamolution/shared';

import '../App.css';
import '../styles/cork-navy.css';
import '../styles/model-bench.css';
import '../components/bench/AgentFirstPersonView.css';
import '../components/shell/TweaksPanel.css';
import { ModelBenchHeader } from '../components/shell/ModelBenchHeader.js';
import { ModelBenchTimeline } from '../components/shell/ModelBenchTimeline.js';
import { computeSquareFramePx } from '../components/bench/board-frame-size.js';
import replayJson from './data/completed-replays.json' with { type: 'json' };
import './BetaApp.css';

type ReplayDecision = {
  agentId: string;
  tick: number;
  action: string;
  prompt: string;
  response: string;
  latencyMs: number;
  cacheEvent: string;
};

type ReplayAgent = {
  id: string;
  x: number;
  y: number;
  alive: boolean;
  sugar: number;
  vision: number;
  metabolism: number;
};

type SugarFrame = {
  tick: number;
  sugar: number[];
  agents: ReplayAgent[];
  alive: number;
  meanSugar: number;
  gini: number;
};

type ReplayWorldBase = {
  id: string;
  kind: 'sugarscape';
  title: string;
  subtitle: string;
  recordedAt: string;
  model: string;
  seed: number;
  totalTicks: number;
  agentCount: number;
  memoryEnabled: boolean;
  reflectionCount: number;
  decisions: ReplayDecision[];
};

type SugarWorld = ReplayWorldBase & {
  kind: 'sugarscape';
  width: number;
  height: number;
  rotationTick: number;
  capacities: number[];
  frames: SugarFrame[];
};

type ReplayWorld = SugarWorld;

const REPLAYS = (replayJson as { worlds: ReplayWorld[] }).worlds;
const WORLDS: ReplayWorld[] = [
  REPLAYS.find((world) => world.id === 'rotating-sugarscape-memory-on')!,
  REPLAYS.find((world) => world.id === 'rotating-sugarscape-memory-off')!,
];

const SPEEDS = [0.5, 1, 2, 4] as const;

function actionLabel(action?: string | null): string {
  if (!action) return 'WAIT';
  return action.replace('move_', '').replaceAll('_', ' ').toUpperCase();
}

function useSquareFrame(): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(520);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const resize = () => setSize(computeSquareFramePx(node.clientWidth, node.clientHeight));
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, size];
}

const CORK_TONES = ['#cfae82', '#c4a172', '#b69162', '#a37f50', '#8c6a40'] as const;

function SugarBoard({ world, frame, selectedAgentId, onSelectAgent, onOpenAgent }: {
  world: SugarWorld;
  frame: SugarFrame;
  selectedAgentId: string | null;
  onSelectAgent: (id: string | null) => void;
  onOpenAgent: () => void;
}) {
  const [stageRef, frameSize] = useSquareFrame();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const livingAgents = frame.agents.filter((agent) => agent.alive);
  const selectedAgent = selectedAgentId === null
    ? null
    : livingAgents.find((agent) => agent.id === selectedAgentId) ?? null;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const context = canvas.getContext('2d');
    if (context === null) return;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    const cellW = rect.width / world.width;
    const cellH = rect.height / world.height;
    frame.sugar.forEach((amount, index) => {
      const x = index % world.width;
      const y = Math.floor(index / world.width);
      const tone = Math.min(4, Math.max(0, Math.round(amount)));
      context.fillStyle = CORK_TONES[tone]!;
      context.fillRect(x * cellW, y * cellH, Math.max(0, cellW - 1), Math.max(0, cellH - 1));
      context.fillStyle = 'rgba(255,255,255,.18)';
      context.fillRect(x * cellW, y * cellH, Math.max(0, cellW - 1), Math.min(1, cellH));
    });
  }, [frame.sugar, frameSize, world.height, world.width]);

  return (
    <div className="board-stage" ref={stageRef}>
      <div className="board-breadcrumb">
        BOARD <span className="board-breadcrumb__dim">/ RECORDED RUN / {world.width} × {world.height} · {frame.alive} AGENTS</span>
      </div>
      <div className="board-frame-wrap" style={{ width: frameSize }}>
        <div className="board-cork-frame">
          <div className="replay-board-surface">
            <canvas ref={canvasRef} data-testid="replay-board-canvas" aria-label={`Rotating Sugarscape resource field at tick ${frame.tick}`} />
            <div className="replay-board-pins">
              {livingAgents.map((agent) => <button
                type="button"
                key={agent.id}
                className={`replay-board-pin${agent.id === selectedAgentId ? ' is-selected' : ''}`}
                data-agent-id={agent.id}
                aria-label={`Inspect ${agent.id}`}
                style={{ left: `${agent.x / world.width * 100}%`, top: `${agent.y / world.height * 100}%`, width: `${100 / world.width}%`, height: `${100 / world.height}%` }}
                onClick={() => onSelectAgent(agent.id === selectedAgentId ? null : agent.id)}
              />)}
            </div>
          </div>
        </div>
        <p className="board-caption">CORK · TONE = SUGAR DENSITY · NAVY PIN = AGENT · CLICK A PIN</p>
      </div>
      {selectedAgent !== null && <>
        <div className="agent-pin-dossier-slot agent-pin-dossier-slot--overlay"><ReplayAgentDossier agent={selectedAgent} tick={frame.tick} onOpen={onOpenAgent} onRelease={() => onSelectAgent(null)} /></div>
        <div className="agent-pin-dossier-slot agent-pin-dossier-slot--stacked" style={{ width: frameSize, maxWidth: '100%' }}><ReplayAgentDossier agent={selectedAgent} tick={frame.tick} onOpen={onOpenAgent} onRelease={() => onSelectAgent(null)} /></div>
      </>}
      {frame.tick === world.rotationTick && <div className="replay-event-stamp">RESOURCE PEAKS ROTATED</div>}
    </div>
  );
}

function AgentReplayView({ world, frame, selectedAgentId }: {
  world: SugarWorld;
  frame: SugarFrame;
  selectedAgentId: string;
}) {
  const agent = frame.agents.find((item) => item.id === selectedAgentId) ?? frame.agents[0]!;
  const radius = agent.vision;
  const patchW = Math.min(world.width, radius * 2 + 1);
  const patchH = Math.min(world.height, radius * 2 + 1);
  const startX = Math.max(0, Math.min(agent.x - radius, world.width - patchW));
  const startY = Math.max(0, Math.min(agent.y - radius, world.height - patchH));
  const cells = [];
  for (let py = 0; py < patchH; py++) {
    for (let px = 0; px < patchW; px++) {
      const x = startX + px;
      const y = startY + py;
      const dx = x - agent.x;
      const dy = y - agent.y;
      const inVision = (dx === 0 && Math.abs(dy) <= radius) || (dy === 0 && Math.abs(dx) <= radius);
      const occupant = inVision
        ? frame.agents.find((item) => item.alive && item.x === x && item.y === y)
        : undefined;
      const tone = Math.min(4, Math.max(0, Math.round(frame.sugar[y * world.width + x] ?? 0)));
      const className = inVision
        ? `afp-cell afp-cell--tone-${tone}${occupant ? ' afp-cell--occupied' : ''}${dx === 0 && dy === 0 ? ' afp-cell--self' : ''}`
        : 'afp-cell afp-cell--fog';
      cells.push(
        <span
          key={`${x}:${y}`}
          data-in-vision={inVision ? 'true' : 'false'}
          data-world-x={x}
          data-world-y={y}
          className={className}
        />,
      );
    }
  }
  return (
    <div className="afp replay-afp">
      <div className="afp-breadcrumb">AGENT / FIRST PERSON / PIN #{agent.id.replace('agent-', '')} / fog = outside vision</div>
      <div className="afp-cork-frame"><div className="afp-grid" data-vision={agent.vision} data-agent-x={agent.x} data-agent-y={agent.y} style={{ gridTemplateColumns: `repeat(${patchW}, 28px)` }}>{cells}</div></div>
      <div className="afp-stats">
        <span>SUGAR<strong>{agent.sugar.toFixed(0)}</strong></span>
        <span>VISION<strong>{agent.vision}</strong></span>
        <span>METABOLISM<strong>{agent.metabolism}</strong></span>
        <span>STATE<strong>{agent.alive ? 'ALIVE' : 'ENDED'}</strong></span>
      </div>
      <p className="afp-hint">CARDINAL RAYS = RECORDED VISION · FOG = OUTSIDE THE AGENT'S VIEW</p>
    </div>
  );
}

function ReplayAgentDossier({ agent, tick, onOpen, onRelease }: {
  agent: ReplayAgent;
  tick: number;
  onOpen: () => void;
  onRelease: () => void;
}) {
  return <div className="agent-pin-dossier">
    <div className="agent-pin-dossier__head"><span className="agent-pin-dossier__id">PIN #{agent.id.replace('agent-', '')}</span><span className="agent-pin-dossier__age">alive {tick}t</span></div>
    <div className="agent-pin-dossier__grid"><span>sugar <strong>{agent.sugar.toFixed(1)}</strong></span><span>vision <strong>{agent.vision}</strong></span><span>metab <strong>{agent.metabolism}</strong></span><span>cell <strong>({agent.x},{agent.y})</strong></span></div>
    <div className="agent-pin-dossier__actions"><button type="button" className="cn-btn cn-btn--primary" onClick={onOpen}>Open first person</button><button type="button" className="cn-btn cn-btn--ghost" onClick={onRelease}>Release</button></div>
  </div>;
}

function Spark({ values, steel = false }: { values: number[]; steel?: boolean }) {
  const max = Math.max(...values, 1);
  return <div className="instruments__spark">{values.slice(-28).map((value, index) => <span key={index} className={`instruments__spark-bar${steel ? ' instruments__spark-bar--steel' : ''}`} style={{ height: `${Math.max(5, value / max * 100)}%` }} />)}</div>;
}

function DecisionInspector({ world, tick, selectedAgentId }: { world: ReplayWorld; tick: number; selectedAgentId: string }) {
  const decision = world.decisions.find((item) => item.tick === tick && item.agentId === selectedAgentId)
    ?? [...world.decisions].reverse().find((item) => item.tick <= tick && item.agentId === selectedAgentId);
  return (
    <>
      <div className="replay-inspector-card">
        <span>MODEL SAW · TICK {decision?.tick ?? '—'}</span>
        <pre>{decision?.prompt ?? 'No model decision was recorded for this agent before this frame.'}</pre>
      </div>
      <div className="replay-inspector-card replay-inspector-card--decision">
        <span>MODEL CHOSE · RECORDED</span>
        <b>{actionLabel(decision?.action)}</b>
        <p>{decision?.response || '—'}</p>
      </div>
    </>
  );
}

function ReplayInstruments({ world, tick, selectedAgentId, benchView, onOpenAgent, onReleaseAgent }: {
  world: ReplayWorld;
  tick: number;
  selectedAgentId: string | null;
  benchView: BenchView;
  onOpenAgent: () => void;
  onReleaseAgent: () => void;
}) {
  const decisionCount = world.decisions.filter((item) => item.tick <= tick).length;
  if (benchView === 'agent' && selectedAgentId !== null) return <div className="instruments"><div className="instruments__panel-title">AGENT INSTRUMENTS</div><DecisionInspector world={world} tick={tick} selectedAgentId={selectedAgentId} /><div className="replay-provenance">VERIFIED V2 CASSETTE<br />{world.model}<br />SEED {world.seed}</div></div>;

  const frame = world.frames[tick] ?? world.frames[0]!;
  const frames = world.frames.slice(0, tick + 1);
  const living = frame.agents.filter((agent) => agent.alive);
  const selectedAgent = selectedAgentId === null ? null : living.find((agent) => agent.id === selectedAgentId) ?? null;
  const bins = Array.from({ length: 12 }, () => 0);
  living.forEach((agent) => { bins[Math.min(11, Math.floor(agent.sugar / 10))]! += 1; });
  const maxBin = Math.max(...bins, 1);
  return <div className="instruments">
    <div className="instruments__panel-title">BENCH INSTRUMENTS</div>
    {benchView === 'board' && selectedAgent !== null && <div className="agent-pin-dossier-slot agent-pin-dossier-slot--sidebar"><ReplayAgentDossier agent={selectedAgent} tick={tick} onOpen={onOpenAgent} onRelease={onReleaseAgent} /></div>}
    <div className="instruments__block"><div className="instruments__metric-row"><span>POPULATION</span><b>{frame.alive} <span className="instruments__metric-dim">/ {world.agentCount}</span></b></div><Spark values={frames.map((item) => item.alive)} /></div>
    <div className="instruments__block"><div className="instruments__metric-row"><span>MEAN SUGAR</span><b className="instruments__metric-steel">{frame.meanSugar.toFixed(1)}</b></div><Spark steel values={frames.map((item) => item.meanSugar)} /></div>
    <div className="instruments__block"><div className="instruments__metric-row"><span>DISTRIBUTION</span><b>GINI {frame.gini.toFixed(3)}</b></div><div className="instruments__hist">{bins.map((value, index) => <span key={index} className="instruments__hist-bar" style={{ height: `${Math.max(3, value / maxBin * 100)}%` }} />)}</div></div>
    <div className="instruments__log"><p className="instruments__log-title">BENCH LOG · {decisionCount} DECISIONS</p>{world.decisions.filter((item) => item.tick <= tick).slice(-10).reverse().map((item) => <p className="instruments__log-line" key={`${item.tick}-${item.agentId}`}><span className="instruments__log-tick">T+{item.tick}</span> {item.agentId.toUpperCase()} · {actionLabel(item.action)}</p>)}</div>
  </div>;
}

function ReplayLibrary({ world, benchView, onBenchViewChange, onChoose, onClose, onOpenTweaks }: { world: ReplayWorld; benchView: BenchView; onBenchViewChange: (view: BenchView) => void; onChoose: (world: ReplayWorld) => void; onClose: () => void; onOpenTweaks: () => void }) {
  return <>
    <div className="utility-backdrop" onClick={onClose} />
    <aside className="utility-drawer replay-library" aria-label="Completed worlds">
      <div className="utility-drawer__title">Replay library</div>
      <div className="replay-library__intro">Completed runs reconstructed from their recorded model calls. No live inference.</div>
      <div className="utility-drawer__group"><span className="utility-drawer__label">BENCH VIEW</span><div className="cn-segment"><button type="button" className={benchView === 'board' ? 'cn-segment__btn--active' : 'cn-segment__btn'} onClick={() => onBenchViewChange('board')}>BOARD</button><button type="button" className="cn-segment__btn" disabled>SECTIONS</button><button type="button" className={benchView === 'agent' ? 'cn-segment__btn--active' : 'cn-segment__btn'} onClick={() => onBenchViewChange('agent')}>AGENT</button></div></div>
      <div className="utility-drawer__group"><span className="utility-drawer__label">EXPERIENCE</span><div className="cn-segment"><button type="button" className="cn-segment__btn--active">Model Bench</button><button type="button" className="cn-segment__btn" disabled>Garden</button></div></div>
      <div className="utility-drawer__group"><span className="utility-drawer__label">COMPLETED WORLDS</span>{WORLDS.map((item, index) => <button type="button" data-testid="replay-world-option" key={item.id} className={`replay-library__world${item.id === world.id ? ' is-active' : ''}`} onClick={() => onChoose(item)}><i>{String(index + 1).padStart(2, '0')}</i><span><b>Rotating Sugarscape</b><small>{item.subtitle}</small></span><em>{item.totalTicks}T</em></button>)}</div>
      <div className="utility-drawer__group"><span className="utility-drawer__label">RUN MODULES</span><div className="replay-module-status"><span>LLM</span><b>RECORDED</b><span>MEMORY</span><b>{world.memoryEnabled ? 'ON' : 'OFF'}</b></div></div>
      <div className="utility-drawer__group"><span className="utility-drawer__label">TUNING</span><button type="button" className="utility-action" onClick={onOpenTweaks}>Replay controls</button></div>
      <div className="replay-library__note"><b>RECORDED REPLAY</b><p>Frames and agent choices come from completed Gamolution runs. Playback never calls a model.</p></div>
      <button type="button" className="utility-action" onClick={onClose}>Close library</button>
    </aside>
  </>;
}

function ReplayTweaksPanel({ open, speed, onSpeedChange, onClose }: { open: boolean; speed: number; onSpeedChange: (speed: number) => void; onClose: () => void }) {
  if (!open) return null;
  return <aside className="tweaks-panel" role="complementary" aria-label="Replay controls">
    <div className="tweaks-panel__head"><span className="tweaks-panel__title">Replay controls</span><button type="button" className="tweaks-panel__close" onClick={onClose} aria-label="Close replay controls">×</button></div>
    <div className="tweaks-panel__field"><div className="tweaks-panel__label-row"><span className="tweaks-panel__label">playbackRate</span><span className="tweaks-panel__value">{speed.toFixed(1)}×</span></div><input type="range" className="tweaks-panel__range" min={0.5} max={4} step={0.5} value={speed} onChange={(event) => onSpeedChange(Number(event.target.value))} /><div className="tweaks-panel__hint">0.5 – 4.0× recorded tick playback</div></div>
    <div className="tweaks-panel__field"><div className="tweaks-panel__label-row"><span className="tweaks-panel__label">decisionSource</span><span className="tweaks-panel__value">CASSETTE</span></div><input type="range" className="tweaks-panel__range" min={1} max={1} value={1} disabled readOnly /><div className="tweaks-panel__hint">Locked to the original model responses</div></div>
  </aside>;
}

export function BetaApp() {
  const [worldId, setWorldId] = useState(WORLDS[0]!.id);
  const [playing, setPlaying] = useState(true);
  const [tick, setTick] = useState(1);
  const [speedIndex, setSpeedIndex] = useState(1);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [benchView, setBenchView] = useState<BenchView>('board');
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [tweaksOpen, setTweaksOpen] = useState(false);
  const [mobileInstrumentsOpen, setMobileInstrumentsOpen] = useState(false);
  const world = WORLDS.find((item) => item.id === worldId) ?? WORLDS[0]!;
  const speed = SPEEDS[speedIndex]!;

  useEffect(() => {
    if (!playing) return;
    const timer = window.setInterval(() => setTick((value) => value >= world.totalTicks ? 1 : value + 1), 900 / speed);
    return () => window.clearInterval(timer);
  }, [playing, speed, world.totalTicks]);

  const sugarFrame = world.frames[tick] ?? world.frames[0]!;
  useEffect(() => {
    if (!sugarFrame) return;
    if (selectedAgentId === null) return;
    const selected = sugarFrame.agents.find((agent) => agent.id === selectedAgentId);
    if (selected?.alive) return;
    const firstLiving = sugarFrame.agents.find((agent) => agent.alive);
    if (firstLiving) setSelectedAgentId(firstLiving.id);
  }, [selectedAgentId, sugarFrame]);

  const chooseWorld = (next: ReplayWorld) => {
    setWorldId(next.id);
    setTick(1);
    setPlaying(true);
    setBenchView('board');
    setSelectedAgentId(null);
    setLibraryOpen(false);
  };
  const timelineSeries = world.frames.map((frame) => frame.alive / world.agentCount);
  const headerSubtitle = `${world.subtitle} · recorded run · seed ${world.seed} · ${world.agentCount} agents`;

  const headerTitle = 'Sugarscape G1 · Model 042';
  return <div className="cn-app replay-app">
    <ModelBenchHeader
      title={headerTitle}
      subtitle={headerSubtitle}
      tickLabel="T"
      tick={tick}
      playing={playing}
      statusLabel={`RECORDED · T +${tick}`}
      primaryActionLabel={playing ? 'Pause replay' : 'Resume replay'}
      onInterveneResume={() => setPlaying((value) => !value)}
      onOpenUtility={() => setLibraryOpen(true)}
      tweaksOpen={tweaksOpen}
      onOpenTweaks={() => setTweaksOpen(true)}
      benchViews={[
        { id: 'board', label: 'BOARD · COMBINED', enabled: true },
        { id: 'layers', label: 'SECTIONS · LAYERED', enabled: false },
        { id: 'agent', label: 'AGENT · FIRST PERSON', enabled: true },
      ]}
      benchView={benchView}
      onBenchViewChange={setBenchView}
      layersUnavailableReason="Recorded layers are not available for this run"
      showViewToggle
    />
    <main className="cn-main">
      <section className="cn-canvas">
        {benchView === 'board' && <SugarBoard world={world} frame={sugarFrame} selectedAgentId={selectedAgentId} onSelectAgent={setSelectedAgentId} onOpenAgent={() => setBenchView('agent')} />}
        {benchView === 'agent' && selectedAgentId !== null && <AgentReplayView world={world} frame={sugarFrame} selectedAgentId={selectedAgentId} />}
        {benchView === 'agent' && selectedAgentId === null && <div className="board-stage"><div className="board-breadcrumb">AGENT · FIRST PERSON</div><div className="cn-empty-view">Select a living agent from the board.</div></div>}
      </section>
      <aside className={`cn-instruments${mobileInstrumentsOpen ? ' cn-instruments--mobile-open' : ''}`}><ReplayInstruments world={world} tick={tick} selectedAgentId={selectedAgentId} benchView={benchView} onOpenAgent={() => setBenchView('agent')} onReleaseAgent={() => setSelectedAgentId(null)} /></aside>
    </main>
    <button type="button" className="instruments-mobile-tab" onClick={() => setMobileInstrumentsOpen((value) => !value)}>{mobileInstrumentsOpen ? 'Hide instruments' : 'Instruments'}</button>
    <ModelBenchTimeline
      tick={tick}
      playing={playing}
      speed={speed}
      ticking={false}
      metricsHistory={[]}
      series={timelineSeries}
      rateUnit="REPLAY"
      tickUnit="TICK"
      maxTick={world.totalTicks}
      hintLines={['SCRUB TO REPLAY', 'RECORDED RUN · READ ONLY']}
      onScrub={(nextTick) => { setPlaying(false); setTick(nextTick); }}
      onPlayPause={() => setPlaying((value) => !value)}
      onStep={() => setTick((value) => Math.min(world.totalTicks, value + 1))}
      onSpeedChange={(nextSpeed) => {
        const index = SPEEDS.findIndex((candidate) => candidate >= nextSpeed);
        setSpeedIndex(index < 0 ? SPEEDS.length - 1 : index);
      }}
    />
    <ReplayTweaksPanel open={tweaksOpen} speed={speed} onSpeedChange={(nextSpeed) => { const index = SPEEDS.findIndex((candidate) => candidate >= nextSpeed); setSpeedIndex(index < 0 ? SPEEDS.length - 1 : index); }} onClose={() => setTweaksOpen(false)} />
    {libraryOpen && <ReplayLibrary world={world} benchView={benchView} onBenchViewChange={(view) => { setBenchView(view); setLibraryOpen(false); }} onChoose={chooseWorld} onClose={() => setLibraryOpen(false)} onOpenTweaks={() => { setLibraryOpen(false); setTweaksOpen(true); }} />}
  </div>;
}
